import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ScanLine, Search, Calendar, Truck, User, Clock, Eye, ChevronLeft,
  ChevronRight, Loader2, ImageOff, Download, Trash2, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SHIFT_LABELS = { A: '1st (A)', B: '2nd (B)', C: '3rd (C)' };

export default function ArchivedScansPage() {
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [doorNoFilter, setDoorNoFilter] = useState('');

  const [selectedCard, setSelectedCard] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchCards = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = { page: pageNum, limit: LIMIT };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (doorNoFilter.trim()) params.door_no = doorNoFilter.trim();

      const res = await axios.get(`${API}/production/scanned-cards`, {
        headers: authHeaders(),
        params,
      });
      setCards(res.data.cards || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load archived scans');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, doorNoFilter]);

  useEffect(() => {
    fetchCards(1);
    setPage(1);
  }, [fetchCards]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchCards(newPage);
  };

  const openCard = async (card) => {
    setSelectedCard(card);
    setImageData(null);
    setLoadingImage(true);
    try {
      const res = await axios.get(`${API}/production/scanned-cards/${card.id}/image`, {
        headers: authHeaders(),
      });
      setImageData(res.data);
    } catch {
      toast.error('Could not load image');
    } finally {
      setLoadingImage(false);
    }
  };

  // ── Download ZIP ──────────────────────────────────────────────────────────
  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (doorNoFilter.trim()) params.door_no = doorNoFilter.trim();

      const res = await axios.get(`${API}/production/scanned-cards/download-zip`, {
        headers: authHeaders(),
        params,
        responseType: 'blob',
        timeout: 120000,
      });

      // Derive filename from Content-Disposition header or fallback
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'scanned_cards.zip';

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${total} scanned card${total !== 1 ? 's' : ''} as ZIP`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        toast.error('No scanned cards found for the selected filters');
      } else {
        toast.error('Download failed — please try again');
      }
    } finally {
      setDownloading(false);
    }
  };

  // ── Bulk Delete ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (doorNoFilter.trim()) params.door_no = doorNoFilter.trim();

      const res = await axios.delete(`${API}/production/scanned-cards/bulk-delete`, {
        headers: authHeaders(),
        params,
      });
      toast.success(`Deleted ${res.data.deleted} scanned card${res.data.deleted !== 1 ? 's' : ''} successfully`);
      setShowDeleteConfirm(false);
      fetchCards(1);
      setPage(1);
    } catch {
      toast.error('Delete failed — please try again');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const hasFilters = startDate || endDate || doorNoFilter;

  const filterDescription = () => {
    const parts = [];
    if (startDate && endDate) parts.push(`${startDate} to ${endDate}`);
    else if (startDate) parts.push(`from ${startDate}`);
    else if (endDate) parts.push(`until ${endDate}`);
    if (doorNoFilter) parts.push(`Door No. "${doorNoFilter}"`);
    return parts.length ? parts.join(', ') : 'ALL records';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center">
            <ScanLine className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Scanned Cards Archive</h1>
            <p className="text-sm text-slate-500">Audit trail of all AI-scanned trip cards</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-slate-600 border-slate-300 px-3 py-1">
            {total} record{total !== 1 ? 's' : ''}
          </Badge>
          <Button
            variant="outline"
            onClick={handleDownloadZip}
            disabled={downloading || total === 0}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="download-zip-btn"
          >
            {downloading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing...</>
              : <><Download className="h-4 w-4 mr-2" /> Download ZIP</>
            }
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={total === 0}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="delete-records-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete Records
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="pl-9"
              data-testid="filter-start-date"
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="pl-9"
              data-testid="filter-end-date"
            />
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={doorNoFilter}
              onChange={e => setDoorNoFilter(e.target.value)}
              placeholder="Door/Truck No..."
              className="pl-9"
              data-testid="filter-door-no"
            />
          </div>
        </div>
        {hasFilters && (
          <button
            onClick={() => { setStartDate(''); setEndDate(''); setDoorNoFilter(''); }}
            className="mt-3 text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="clear-filters-btn"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
            <ScanLine className="h-12 w-12" />
            <p className="font-medium">No scanned cards found</p>
            <p className="text-sm">Scanned trip cards will appear here after using the AI scanner</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Shift</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Door No</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Operator</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">Trips</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Scanned By</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Scanned At</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cards.map(card => (
                  <tr key={card.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-800">{card.date || '—'}</td>
                    <td className="px-4 py-3">
                      {card.shift ? (
                        <Badge
                          className={
                            card.shift === 'A' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                            card.shift === 'B' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                            'bg-purple-100 text-purple-800 border-purple-200'
                          }
                          variant="outline"
                        >
                          {SHIFT_LABELS[card.shift] || card.shift}
                        </Badge>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 text-slate-700">
                        <Truck className="h-3.5 w-3.5 text-slate-400" />
                        {card.door_no || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <span className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        {card.operator_name || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                        {card.trips_count ?? 0}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{card.scanned_by || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {card.scanned_at
                          ? new Date(card.scanned_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })
                          : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openCard(card)}
                        className="h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`view-scan-${card.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-500">
                  Page {page} of {totalPages} · {total} total records
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Scanned Cards?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              <p className="font-semibold mb-1">This action cannot be undone.</p>
              <p>
                You are about to permanently delete <span className="font-bold">{total} record{total !== 1 ? 's' : ''}</span>
                {' '}({filterDescription()}).
              </p>
              {!hasFilters && (
                <p className="mt-2 font-semibold">Warning: No filters applied — this will delete ALL scanned cards!</p>
              )}
            </div>
            <p className="text-sm text-slate-600">
              Tip: Download the ZIP first to keep a local backup before deleting.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...</>
                : <><Trash2 className="h-4 w-4 mr-2" /> Delete {total} Record{total !== 1 ? 's' : ''}</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!selectedCard} onOpenChange={open => { if (!open) { setSelectedCard(null); setImageData(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5 text-blue-600" />
              Scanned Card — Door No. {selectedCard?.door_no || '?'} · {selectedCard?.date || ''}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 p-1">
            {/* Meta info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 rounded-lg p-3">
              <div>
                <p className="text-xs text-slate-500">Date</p>
                <p className="font-medium text-slate-800">{selectedCard?.date || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Shift</p>
                <p className="font-medium text-slate-800">{selectedCard?.shift ? SHIFT_LABELS[selectedCard.shift] || selectedCard.shift : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Door No</p>
                <p className="font-medium text-slate-800">{selectedCard?.door_no || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Operator</p>
                <p className="font-medium text-slate-800">{selectedCard?.operator_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Trips Extracted</p>
                <p className="font-medium text-slate-800">{selectedCard?.trips_count ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Scanned By</p>
                <p className="font-medium text-slate-800 text-xs">{selectedCard?.scanned_by || '—'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500">Scanned At</p>
                <p className="font-medium text-slate-800 text-xs">
                  {selectedCard?.scanned_at
                    ? new Date(selectedCard.scanned_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })
                    : '—'}
                </p>
              </div>
            </div>

            {/* Image */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Original Trip Card Image</p>
              <div className="border rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center min-h-[200px]">
                {loadingImage ? (
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                ) : imageData?.image_base64 ? (
                  <img
                    src={`data:${imageData.image_content_type || 'image/jpeg'};base64,${imageData.image_base64}`}
                    alt="Scanned trip card"
                    className="max-w-full max-h-96 object-contain"
                    data-testid="scanned-card-image"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-slate-400 py-8">
                    <ImageOff className="h-10 w-10" />
                    <p className="text-sm">Image not available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Extracted trips */}
            {selectedCard?.extracted_data?.trips?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Extracted Trips ({selectedCard.extracted_data.trips.length})
                </p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b">
                        <th className="text-center px-3 py-2 text-slate-600 w-10">#</th>
                        <th className="text-left px-3 py-2 text-slate-600">L Time</th>
                        <th className="text-left px-3 py-2 text-slate-600">UL Time</th>
                        <th className="text-left px-3 py-2 text-slate-600">Machine No</th>
                        <th className="text-left px-3 py-2 text-slate-600">Material</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedCard.extracted_data.trips.map((trip, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="text-center px-3 py-2 text-slate-500 text-xs">{trip.serial ?? i + 1}</td>
                          <td className="px-3 py-2 text-slate-700">{trip.l_time || '—'}</td>
                          <td className="px-3 py-2 text-slate-700">{trip.ul_time || '—'}</td>
                          <td className="px-3 py-2 text-slate-700">{trip.machine_no || '—'}</td>
                          <td className="px-3 py-2 text-slate-500">{trip.material || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
